import { formatCount, intlLocale } from "@ghezaa/ui";
import type { Period, ReportAcross, ReportCatalogue, ReportColumn, ReportDimension, ReportMeasure, ReportSpec } from "@/api/client";
import { day, money, percent } from "./format";

/** What "New report" opens on: this month by salesperson, against the same days of last month. */
export const newSpec: ReportSpec = {
  rows: "Salesperson",
  then: null,
  measures: ["Revenue", "GrossProfit", "Margin"],
  period: "ThisMonth",
  from: null,
  to: null,
  compare: "PreviousPeriod",
  across: "None",
  filters: [],
};

const timeDimensions: ReportDimension[] = ["Day", "Week", "Month", "Quarter", "Year"];
export const isTime = (d: ReportDimension | null) => d !== null && timeDimensions.includes(d);

/** Of target is by salesperson only: rows by salesperson, nothing under them, not across. */
export const attainmentFits = (spec: ReportSpec) => spec.rows === "Salesperson" && spec.then === null && spec.across === "None";

/**
 * The definition with everything that no longer fits a change taken out, so a choice never leaves the report unable to
 * run: a second dimension equal to the rows, Of target away from salespeople, more than one measure across the calendar.
 */
export function tidy(spec: ReportSpec): ReportSpec {
  const next = { ...spec, filters: spec.filters.filter((f) => f.values.length > 0) };
  if (next.then === next.rows) next.then = null;
  if (next.across !== "None" && (isTime(next.rows) || isTime(next.then))) next.across = "None";
  if (next.across !== "None") {
    const first = next.measures.find((m) => m !== "Attainment") ?? "Revenue";
    next.measures = [first];
    next.compare = "None";
  }
  if (!attainmentFits(next)) next.measures = next.measures.filter((m) => m !== "Attainment");
  if (next.measures.length === 0) next.measures = ["Revenue"];
  if (next.period !== "Custom") {
    next.from = null;
    next.to = null;
  }
  return next;
}

/** The same definition written the same way, to tell a saved view from its edited copy. */
export function sameSpec(a: ReportSpec | null | undefined, b: ReportSpec | null | undefined): boolean {
  if (!a || !b) return a === b;
  const flat = (s: ReportSpec) =>
    JSON.stringify([s.rows, s.then, s.measures, s.period, s.from, s.to, s.compare, s.across, s.filters.map((f) => [f.dimension, [...f.values].sort()])]);
  return flat(a) === flat(b);
}

/** A definition read back from an address or a saved view: anything missing takes the new report's value. */
export function specOf(value: unknown): ReportSpec | undefined {
  if (!value || typeof value !== "object") return undefined;
  const v = value as Partial<ReportSpec>;
  if (typeof v.rows !== "string" || !Array.isArray(v.measures) || typeof v.period !== "string") return undefined;
  return {
    rows: v.rows,
    then: typeof v.then === "string" ? v.then : null,
    measures: v.measures.filter((m): m is ReportMeasure => typeof m === "string"),
    period: v.period,
    from: typeof v.from === "string" ? v.from : null,
    to: typeof v.to === "string" ? v.to : null,
    compare: typeof v.compare === "string" ? v.compare : "None",
    across: typeof v.across === "string" ? v.across : "None",
    filters: Array.isArray(v.filters)
      ? v.filters.filter((f) => f && typeof f.dimension === "string" && Array.isArray(f.values)).map((f) => ({ dimension: f.dimension, values: f.values.map(String) }))
      : [],
  };
}

/** A report in a few words for a list: "By salesperson · Revenue, Gross profit +1 · This week". */
export function summary(spec: ReportSpec, catalogue: ReportCatalogue | undefined): string {
  if (!catalogue) return "";
  const dimension = (d: ReportDimension) => catalogue.dimensions.find((x) => x.key === d)?.label.toLowerCase() ?? d;
  const measure = (m: ReportMeasure) => catalogue.measures.find((x) => x.key === m)?.label ?? m;
  const rows = `By ${dimension(spec.rows)}${spec.then ? ` then ${dimension(spec.then)}` : ""}`;
  const shown = spec.measures.slice(0, 2).map(measure).join(", ") + (spec.measures.length > 2 ? ` +${spec.measures.length - 2}` : "");
  const across = spec.across !== "None" ? ` by ${spec.across.toLowerCase()}` : "";
  const period = catalogue.periods.find((p) => p.key === spec.period)?.label ?? spec.period;
  return [rows, shown + across, period].join(" · ");
}

/** The days of a period as one phrase: `1 Nov 2026`, `1–5 Nov 2026`, `29 Oct – 4 Nov 2026`. */
export function daysOf(period: Period): string {
  if (period.from === period.to) return day(period.from);
  const [fy, fm] = period.from.split("-");
  const [ty, tm] = period.to.split("-");
  const end = day(period.to);
  if (fy === ty && fm === tm) return `${Number(period.from.slice(8))}–${end}`;
  if (fy === ty) return `${day(period.from).replace(/\s\d{4}$/, "")} – ${end}`;
  return `${day(period.from)} – ${end}`;
}

/** A column's header: money names its currency once; a figure compared with the period before reads "Before", then "Change". */
export function header(column: ReportColumn, currency: string, across: ReportAcross): string {
  if (column.kind === "before") return "Before";
  if (column.kind === "change") return "Change";
  if (column.kind === "bucket" && column.key.startsWith("p:")) return bucketLabel(column.key.slice(2), across, column.label);
  const money = column.format === "money" && (column.kind === "measure" || column.kind === "total");
  return money && currency ? `${column.label} (${currency})` : column.label;
}

function bucketLabel(key: string, across: ReportAcross, fallback: string): string {
  if (across === "Month") {
    const [y, m] = key.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: "short", year: "2-digit" });
  }
  if (across === "Week") return day(key).replace(/\s\d{4}$/, "");
  return fallback;
}

/**
 * A figure as the column shows it. In the rows a zero is left empty, so only what differs is marked; the total keeps
 * it. A change carries its sign: `+12.5%`, or points for a share (`−0.8 pts`).
 */
export function figure(column: ReportColumn, value: number | null, inTotal = false): string {
  if (value === null) return "";
  if (column.kind === "change") {
    const points = column.format === "points";
    const size = Math.round(Math.abs(value) * 1000) / 1000;   // what one decimal of a percentage shows
    const text = points
      ? `${(size * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} pts`
      : size.toLocaleString(intlLocale(), { style: "percent", maximumFractionDigits: 1 });
    return size === 0 ? text : `${value > 0 ? "+" : "−"}${text}`;
  }
  if (value === 0 && !inTotal && column.format !== "percent") return "";
  switch (column.format) {
    case "money":
      return money(value);
    case "percent":
      return percent(value);
    case "count":
      return formatCount(value);
    case "decimal":
      return value.toLocaleString(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    case "quantity":
      return value.toLocaleString(intlLocale(), { maximumFractionDigits: 3 });
    default:
      return String(value);
  }
}
